/** @jsx jsx */
import React, { type ChangeEvent, useCallback } from 'react';
import { styled, jsx } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import {
	type KeyboardOrMouseEvent,
	ModalTransition,
	ModalHeader,
	ModalTitle,
	ModalBody,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import { Box, xcss } from '@atlaskit/primitives';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { ModalDialog } from '@atlassian/jira-software-modal-dialog';
import type { TimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter';
import type { EstimationType } from '../../../../model/types';
import type { Assignee } from '../types';
import messages from './messages';
import SummaryTable from './summary-table';

export type Props = {
	isOpen: boolean;
	onToggleModal: () => void;
	sprintName: string;
	assignees: Assignee[];
	unassigned?: Assignee;
	isEstimationEnabled: boolean;
	isRemainingTimeTrackingEnabled: boolean;
	estimationType: EstimationType;
	timeTrackingOptions: TimeTrackingOptions;
	estimationFieldName: string;
	includeSubtasks: boolean;
	onToggleIncludeSubtasks: (
		event: ChangeEvent<HTMLInputElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
};

const SummaryModal = (props: Props) => {
	const {
		isOpen,
		onToggleModal,
		sprintName,
		unassigned,
		assignees,
		isEstimationEnabled,
		isRemainingTimeTrackingEnabled,
		estimationType,
		timeTrackingOptions,
		estimationFieldName,
		includeSubtasks,
		onToggleIncludeSubtasks,
	} = props;

	const { formatMessage } = useIntl();

	const onCloseModal = useCallback(
		(event: KeyboardOrMouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			onToggleModal();
			fireUIAnalytics(analyticsEvent, 'closeWorkloadModal');
		},
		[onToggleModal],
	);

	return (
		<ModalTransition>
			{isOpen && (
				<ModalDialog onClose={onCloseModal} width="medium">
					<ModalHeader>
						<ModalTitle>{formatMessage(messages.modalHeader)}</ModalTitle>
					</ModalHeader>

					<ModalBody>
						<Box xcss={sprintNameStyles}>
							<Heading size="xsmall">{sprintName}</Heading>
						</Box>

						<ToggleWrapper>
							<Toggle
								id="include_subtasks"
								isChecked={includeSubtasks}
								onChange={onToggleIncludeSubtasks}
							/>
							{formatMessage(messages.includeSubtasksToggleLabel)}
						</ToggleWrapper>
						<SummaryTable
							unassigned={unassigned}
							assignees={assignees}
							isEstimationEnabled={isEstimationEnabled}
							isRemainingTimeTrackingEnabled={isRemainingTimeTrackingEnabled}
							estimationType={estimationType}
							timeTrackingOptions={timeTrackingOptions}
							estimationFieldName={estimationFieldName}
							includeSubtasks={includeSubtasks}
						/>
					</ModalBody>
					<ModalFooter>
						<Button
							aria-label={formatMessage(messages.closeModalButtonAriaLabel)}
							appearance="primary"
							onClick={onCloseModal}
							autoFocus
						>
							{formatMessage(messages.closeModalButtonLabel)}
						</Button>
					</ModalFooter>
				</ModalDialog>
			)}
		</ModalTransition>
	);
};

export default SummaryModal;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ToggleWrapper = styled.label({
	display: 'flex',
	alignItems: 'center',
	marginLeft: token('space.negative.075', '-6px'),
});

const sprintNameStyles = xcss({
	paddingBottom: 'space.200',
});
